import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Flag from "@/components/Flag";
import EditableSubtitle from "@/components/EditableSubtitle";
import { computeGroupStandings } from "@/lib/groupLogic";
import { useDateTimeFormat } from "@/lib/TzContext";
import { useI18n } from "@/lib/I18nContext";
import { teamLabel } from "@/lib/teamNames";
import { Lock, CheckCircle2 } from "lucide-react";

const GROUP_LETTERS = ["A","B","C","D","E","F","G","H","I","J","K","L"];

export default function Fase1Grupos() {
  // Tab values: "torneo" | "todos" | "A" | "B" | ... | "L"
  const [tab, setTab] = useState("torneo");
  const { t } = useI18n();
  const [groups, setGroups] = useState([]);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [preds, setPreds] = useState({});
  const [tournament, setTournament] = useState({ champion: "", runner_up: "", third: "" });
  const [loaded, setLoaded] = useState(false);
  const [appState, setAppState] = useState({ predictions_published: false });

  useEffect(() => {
    (async () => {
      const [g, m, t, mp, st] = await Promise.all([
        api.get("/tournament/groups"),
        api.get("/tournament/matches"),
        api.get("/tournament/teams"),
        api.get("/predictions/me"),
        api.get("/app-state"),
      ]);
      setGroups(g.data);
      setMatches(m.data);
      setTeams(t.data);
      setAppState(st.data);
      const map = {};
      (mp.data.matches || []).forEach((p) => { map[p.match_id] = p; });
      setPreds(map);
      setTournament({
        champion: mp.data.tournament?.champion || "",
        runner_up: mp.data.tournament?.runner_up || "",
        third: mp.data.tournament?.third || "",
      });
      setLoaded(true);
    })();
  }, []);

  const teamByCode = useMemo(() => Object.fromEntries(teams.map((t) => [t.code, t])), [teams]);
  const groupMatches = useMemo(() => matches.filter((m) => m.stage === "group"), [matches]);
  const standingsByGroup = useMemo(() => {
    const out = {};
    groups.forEach((g) => {
      out[g.group] = computeGroupStandings(g.group, g.teams.map((t) => t.code), groupMatches, Object.values(preds));
    });
    return out;
  }, [groups, groupMatches, preds]);

  const allGroupMatchesSorted = useMemo(
    () => [...groupMatches].sort((a, b) => {
      const ka = new Date(a.kickoff || 0).getTime();
      const kb = new Date(b.kickoff || 0).getTime();
      return ka - kb || a.match_id.localeCompare(b.match_id);
    }),
    [groupMatches]
  );

  const isLocked = (kickoffIso) => {
    if (appState.predictions_published) return true;
    const lock = new Date(kickoffIso).getTime() - 60 * 60 * 1000;
    return Date.now() >= lock;
  };

  // Per-match save queue. Saves for the same match are chained so a slower
  // earlier PUT can never overwrite a later one on the backend (which would
  // happen with naive parallel calls when the user types home + away fast).
  const saveQueuesRef = React.useRef({});

  const savePred = async (matchId, patch) => {
    // Step 1: optimistic state update using a functional updater so the latest
    // value (not a stale closure) is merged into preds.
    let payload = null;
    setPreds((prev) => {
      const merged = { ...(prev[matchId] || {}), ...patch, match_id: matchId };
      payload = merged;
      return { ...prev, [matchId]: merged };
    });
    // Step 2: chain the PUT behind any in-flight save for this match.
    const prev = saveQueuesRef.current[matchId] || Promise.resolve();
    const next = prev.then(async () => {
      try {
        await api.put("/predictions/match", {
          match_id: matchId,
          home_score: payload.home_score ?? null,
          away_score: payload.away_score ?? null,
          winner_team: payload.winner_team ?? null,
        });
      } catch (e) {
        if (e?.response?.status === 423) toast.error(e.response.data?.detail || t("err.locked"));
        else toast.error(t("err.save"));
      }
    });
    saveQueuesRef.current[matchId] = next;
    await next;
    if (saveQueuesRef.current[matchId] === next) {
      delete saveQueuesRef.current[matchId];
    }
  };

  const saveTournament = async (patch) => {
    const prev = tournament;
    const next = { ...tournament, ...patch };
    setTournament(next);
    try {
      await api.put("/predictions/tournament", next);
    } catch (e) {
      // Revert optimistic update on validation / lock failure
      setTournament(prev);
      const status = e?.response?.status;
      if (status === 423) toast.error(e.response.data?.detail || t("err.locked"));
      else if (status === 400) toast.error(e.response.data?.detail || t("err.save"));
      else toast.error(t("err.save"));
    }
  };

  if (!loaded) return <div className="text-zinc-400">{t("common.loading")}</div>;

  const tabs = [
    { id: "torneo", label: t("phase1.tabs.torneo") },
    { id: "todos", label: t("phase1.tabs.todos") },
    ...GROUP_LETTERS.map((l) => ({ id: l, label: t("phase1.tabs.group", { letter: l }) })),
  ];

  return (
    <div className="space-y-6" data-testid="fase1-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono-data mb-2">{t("phase1.kicker")}</div>
        <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">{t("phase1.title")}</h1>
        <EditableSubtitle
          pageKey="fase1"
          fallback={t("phase1.subtitle.fallback")}
        />
        {appState.predictions_published && (
          <div className="mt-3 p-3 bg-[var(--c-yellow)]/10 border border-[var(--c-yellow)] text-[var(--c-yellow)] text-sm flex items-center gap-2" data-testid="published-banner">
            <CheckCircle2 size={16} /> {t("phase1.published")}
          </div>
        )}
      </div>

      {/* Tabs row */}
      <div className="border-b border-zinc-800 flex gap-1 overflow-x-auto" data-testid="fase1-tabs">
        {tabs.map((s) => (
          <button
            key={s.id}
            data-testid={`fase1-tab-${s.id}`}
            onClick={() => setTab(s.id)}
            className={`px-3 md:px-4 py-3 text-[11px] md:text-xs uppercase tracking-widest font-semibold whitespace-nowrap transition-colors ${
              tab === s.id ? "text-white border-b-2 border-[var(--c-lime)]" : "text-zinc-500 hover:text-white"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {tab === "torneo" && (
        <TournamentPicks teams={teams} value={tournament} onChange={saveTournament} locked={appState.predictions_published} />
      )}

      {tab === "todos" && (
        <AllMatchesList
          matches={allGroupMatchesSorted}
          teamByCode={teamByCode}
          preds={preds}
          isLocked={isLocked}
          savePred={savePred}
        />
      )}

      {GROUP_LETTERS.includes(tab) && (
        <GroupView
          letter={tab}
          group={groups.find((g) => g.group === tab)}
          standings={standingsByGroup[tab] || []}
          groupMatches={groupMatches.filter((m) => m.group === tab)
            .sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff))}
          teamByCode={teamByCode}
          preds={preds}
          isLocked={isLocked}
          savePred={savePred}
        />
      )}
    </div>
  );
}

function MatchRow({ m, teamByCode, preds, locked, savePred }) {
  const p = preds[m.match_id] || {};
  const fmt = useDateTimeFormat();
  const { lang } = useI18n();
  const homeName = teamLabel(m.home, teamByCode[m.home]?.name, lang);
  const awayName = teamLabel(m.away, teamByCode[m.away]?.name, lang);
  return (
    <div className="flex items-center gap-2 text-sm py-1" data-testid={`match-row-${m.match_id}`}>
      <div className="flex-1 min-w-0 flex items-center gap-2 justify-end">
        <span className="text-xs md:text-sm truncate" title={homeName}>{homeName}</span>
        <Flag code={m.home} flag={teamByCode[m.home]?.flag} flagImageUrl={teamByCode[m.home]?.flag_image_url} size={22} />
      </div>
      <input
        type="number" min={0} max={20}
        disabled={locked}
        value={p.home_score ?? ""}
        onChange={(e) => savePred(m.match_id, { home_score: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
        className="score-input"
        data-testid={`score-home-${m.match_id}`}
      />
      <span className="text-zinc-600">·</span>
      <input
        type="number" min={0} max={20}
        disabled={locked}
        value={p.away_score ?? ""}
        onChange={(e) => savePred(m.match_id, { away_score: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
        className="score-input"
        data-testid={`score-away-${m.match_id}`}
      />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <Flag code={m.away} flag={teamByCode[m.away]?.flag} flagImageUrl={teamByCode[m.away]?.flag_image_url} size={22} />
        <span className="text-xs md:text-sm truncate" title={awayName}>{awayName}</span>
      </div>
      <div className="hidden md:flex flex-col items-end text-[10px] font-mono-data text-zinc-500 w-28 text-right">
        <span>{fmt.short(m.kickoff)}</span>
        <span className="text-zinc-600">{fmt.time(m.kickoff)}</span>
        {m.venue && <span className="text-zinc-700 truncate w-full">{m.venue}</span>}
        {locked && <Lock size={10} className="text-[var(--c-red)] mt-0.5" />}
      </div>
    </div>
  );
}

function AllMatchesList({ matches, teamByCode, preds, isLocked, savePred }) {
  const fmt = useDateTimeFormat();
  const { t } = useI18n();
  if (matches.length === 0) return <div className="text-zinc-500 text-sm">{t("phase1.empty")}</div>;
  return (
    <div className="bg-zinc-900 border border-zinc-800" data-testid="fase1-all-list">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="font-display text-lg uppercase">{t("phase1.allTitle")}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">
          {t("phase1.allCount", { count: matches.length })}
        </div>
      </div>
      <div className="divide-y divide-zinc-900">
        {matches.map((m) => (
          <div key={m.match_id} className="px-4 py-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono-data mb-0.5 md:hidden">
              {t("phase1.tabs.group", { letter: m.group })} · {fmt.short(m.kickoff)} · {fmt.time(m.kickoff)} · {m.venue || "—"}
            </div>
            <MatchRow
              m={m}
              teamByCode={teamByCode}
              preds={preds}
              locked={isLocked(m.kickoff)}
              savePred={savePred}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupView({ letter, group, standings, groupMatches, teamByCode, preds, isLocked, savePred }) {
  const { t, lang } = useI18n();
  if (!group) return <div className="text-zinc-500 text-sm">{t("phase1.empty")}</div>;
  return (
    <div className="bg-zinc-900 border border-zinc-800" data-testid={`group-view-${letter}`}>
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[var(--c-lime)] text-black font-display flex items-center justify-center text-xl">{letter}</div>
          <div className="font-display text-2xl uppercase">{t("phase1.tabs.group", { letter })}</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">{t("phase1.kicker")}</div>
      </div>
      <div className="px-4 py-3 border-b border-zinc-800">
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data mb-1">
          <div className="col-span-6">{t("phase1.standings.team")}</div>
          <div className="col-span-2 text-right">{t("phase1.standings.pts")}</div>
          <div className="col-span-2 text-right">{t("phase1.standings.gd")}</div>
          <div className="col-span-2 text-right">{t("phase1.standings.gf")}</div>
        </div>
        {standings.map((row, i) => (
          <div key={row.code} className="grid grid-cols-12 gap-2 py-1 items-center text-sm">
            <div className="col-span-6 flex items-center gap-2 min-w-0">
              <span className={`w-4 text-xs ${i < 2 ? "text-[var(--c-lime)]" : i === 2 ? "text-[var(--c-yellow)]" : "text-zinc-500"}`}>{i + 1}</span>
              <Flag code={row.code} flag={teamByCode[row.code]?.flag} flagImageUrl={teamByCode[row.code]?.flag_image_url} size={20} />
              <span className="text-xs md:text-sm truncate" title={teamLabel(row.code, teamByCode[row.code]?.name, lang)}>{teamLabel(row.code, teamByCode[row.code]?.name, lang)}</span>
            </div>
            <div className="col-span-2 text-right font-mono-data font-bold">{row.pts}</div>
            <div className="col-span-2 text-right font-mono-data text-zinc-400">{row.gd > 0 ? "+" : ""}{row.gd}</div>
            <div className="col-span-2 text-right font-mono-data text-zinc-400">{row.gf}</div>
          </div>
        ))}
      </div>
      <div className="p-4 space-y-2">
        {groupMatches.map((m) => (
          <MatchRow
            key={m.match_id}
            m={m}
            teamByCode={teamByCode}
            preds={preds}
            locked={isLocked(m.kickoff)}
            savePred={savePred}
          />
        ))}
      </div>
    </div>
  );
}

function TournamentPicks({ teams, value, onChange, locked }) {
  const { t, lang } = useI18n();
  const sorted = useMemo(() => {
    return [...teams]
      .map((tm) => ({ ...tm, displayName: teamLabel(tm.code, tm.name, lang) }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [teams, lang]);
  const slot = (label, key, color) => {
    // Teams already chosen in the OTHER two slots — hide them from this dropdown.
    // The currently selected value stays visible so the user sees what's picked.
    const otherKeys = ["champion", "runner_up", "third"].filter((k) => k !== key);
    const taken = new Set(otherKeys.map((k) => value[k]).filter(Boolean));
    const options = sorted.filter((tm) => !taken.has(tm.code) || tm.code === value[key]);
    return (
      <div className="bg-zinc-900 border border-zinc-800 p-6 flex-1" data-testid={`pick-${key}`}>
        <div className="text-[10px] uppercase tracking-widest font-mono-data mb-2" style={{ color }}>{label}</div>
        <select
          value={value[key] || ""}
          disabled={locked}
          onChange={(e) => onChange({ [key]: e.target.value || null })}
          data-testid={`pick-${key}-select`}
          className="w-full bg-black border border-zinc-800 focus:border-[var(--c-lime)] px-3 py-3 outline-none text-sm rounded-sm disabled:opacity-50"
        >
          <option value="">{t("phase1.picks.select")}</option>
          {options.map((tm) => (
            <option key={tm.code} value={tm.code}>{tm.displayName}</option>
          ))}
        </select>
      </div>
    );
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {slot(t("phase1.picks.champion"), "champion", "var(--c-yellow)")}
        {slot(t("phase1.picks.runnerUp"), "runner_up", "var(--c-purple)")}
        {slot(t("phase1.picks.third"), "third", "var(--c-peach)")}
      </div>
    </div>
  );
}
